import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, ChevronUp, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

export default function DocCategories() {
  const { readOnly } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/doc-categories");
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/doc-categories/${id}`, body);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const add = async () => {
    if (readOnly || !adding.trim()) return;
    try {
      await api.post("/admin/doc-categories", { name: adding.trim(), active: true });
      setAdding("");
      toast.success("Category added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reorder = async (idx, dir) => {
    if (readOnly) return;
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const a = rows[idx];
    const b = rows[target];
    try {
      await Promise.all([
        api.patch(`/admin/doc-categories/${a.id}`, { order: b.order }),
        api.patch(`/admin/doc-categories/${b.id}`, { order: a.order }),
      ]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-doc-categories-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Document categories"
        title="Document categories"
        description="Tagging system for case-related uploads. Reorder categories to change picker order."
      />
      {!readOnly && (
        <div className="brand-card p-4">
          <div className="flex items-end gap-2">
            <Input
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              placeholder="New category name"
              className="w-[280px]"
              aria-label="New category name"
              data-testid="doc-category-add-input"
            />
            <Button
              onClick={add}
              data-testid="doc-category-add-submit"
              className="goodwin-primary-btn text-white"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
          </div>
        </div>
      )}
      <div className="brand-card overflow-hidden">
        <ul className="divide-y divide-[color:var(--border-subtle)]">
          {rows.map((r, idx) => (
            <li
              key={r.id}
              className="flex items-center justify-between px-4 py-3"
              data-testid={`doc-category-row-${r.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-[color:var(--page-fg-muted)]">{r.order}</span>
                <span className={r.active ? "text-[color:var(--page-fg)]" : "text-[color:var(--page-fg-muted)] line-through"}>
                  {r.name}
                </span>
                {r.builtin && (
                  <span className="rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[color:var(--page-fg)]">
                    Built-in
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => reorder(idx, -1)}
                  disabled={readOnly || idx === 0}
                  aria-label={`Move ${r.name} up`}
                  className="h-8 w-8"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => reorder(idx, 1)}
                  disabled={readOnly || idx === rows.length - 1}
                  aria-label={`Move ${r.name} down`}
                  className="h-8 w-8"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Switch
                  checked={!!r.active}
                  onCheckedChange={(v) => patch(r.id, { active: v })}
                  disabled={readOnly}
                  aria-label={`Toggle ${r.name} active`}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
